import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

export const HOURS_12 = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
export const ALL_MINUTES = ['00', '15', '30', '45'];

export const DAY_RANGE_OPTIONS = [
  { id: 'mon-fri', label: 'Monday - Friday (Weekdays)', short: 'Mon - Fri', days: [1, 2, 3, 4, 5] },
  { id: 'sat-sun', label: 'Saturday - Sunday (Weekends)', short: 'Sat - Sun', days: [6, 0] },
  { id: 'all-days', label: 'All Days (Monday - Sunday)', short: 'All Days', days: [0, 1, 2, 3, 4, 5, 6] },
  { id: 'mon-wed', label: 'Monday - Wednesday', short: 'Mon - Wed', days: [1, 2, 3] },
  { id: 'thu-fri', label: 'Thursday - Friday', short: 'Thu - Fri', days: [4, 5] },
  { id: 'mon-sat', label: 'Monday - Saturday', short: 'Mon - Sat', days: [1, 2, 3, 4, 5, 6] },
  { id: 'custom', label: 'Custom Days Range...', short: 'Custom', days: [] }
];

export const DAYS_OF_WEEK = [
  { name: 'Monday', short: 'Mon', dayNum: 1 },
  { name: 'Tuesday', short: 'Tue', dayNum: 2 },
  { name: 'Wednesday', short: 'Wed', dayNum: 3 },
  { name: 'Thursday', short: 'Thu', dayNum: 4 },
  { name: 'Friday', short: 'Fri', dayNum: 5 },
  { name: 'Saturday', short: 'Sat', dayNum: 6 },
  { name: 'Sunday', short: 'Sun', dayNum: 0 },
];

export const DEFAULT_SLOTS_LIST = [
  { label: "06:00 AM - 07:00 AM", isActive: true },
  { label: "07:00 AM - 08:00 AM", isActive: true },
  { label: "08:00 AM - 09:00 AM", isActive: true },
  { label: "09:00 AM - 10:00 AM", isActive: true },
  { label: "10:00 AM - 11:00 AM", isActive: true },
  { label: "11:00 AM - 12:00 PM", isActive: true },
  { label: "12:00 PM - 01:00 PM", isActive: true },
  { label: "01:00 PM - 02:00 PM", isActive: true },
  { label: "02:00 PM - 03:00 PM", isActive: true },
  { label: "03:00 PM - 04:00 PM", isActive: true },
  { label: "04:00 PM - 05:00 PM", isActive: true },
  { label: "05:00 PM - 06:00 PM", isActive: true },
  { label: "06:00 PM - 07:00 PM", isActive: true },
  { label: "07:00 PM - 08:00 PM", isActive: true },
  { label: "08:00 PM - 09:00 PM", isActive: true },
  { label: "09:00 PM - 10:00 PM", isActive: true }
];
export const defaultSlotsList = DEFAULT_SLOTS_LIST;

/**
 * Calculates the min and max price range across base rate, day schedules, and custom slot overrides.
 * Returns { min, max, text, hasRange }
 */
export function getTurfPriceRange(turf) {
  if (!turf) return { min: 0, max: 0, text: '₹0', hasRange: false };

  const basePrice = parseFloat(turf.price_per_hour || turf.price) || 0;
  const prices = [];
  if (basePrice > 0) prices.push(basePrice);

  const rules = turf.pricingRules || [];
  let slotsConfig = rules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');

  if (!slotsConfig && turf._id) {
    try {
      const localRules = JSON.parse(localStorage.getItem(`turf_pricing_rules_${turf._id}`) || '[]');
      slotsConfig = localRules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');
    } catch (e) {}
  }

  // Check timings schedule prices
  const timings = turf.timings || slotsConfig?.timings || [];
  if (Array.isArray(timings)) {
    timings.forEach(t => {
      const p = parseFloat(t.price);
      if (!isNaN(p) && p > 0) prices.push(p);
    });
  }

  // Check custom weekday & weekend slot prices
  const weekdaySlots = slotsConfig?.weekdaySlots || [];
  const weekendSlots = slotsConfig?.weekendSlots || [];
  [...weekdaySlots, ...weekendSlots].forEach(s => {
    if (typeof s === 'object' && s && s.isActive !== false) {
      const sp = parseFloat(s.price);
      if (!isNaN(sp) && sp > 0) prices.push(sp);
    }
  });

  // Check other pricing rules
  if (Array.isArray(rules)) {
    rules.forEach(r => {
      if (r.type !== 'slots_config' && r.price) {
        const rp = parseFloat(r.price);
        if (!isNaN(rp) && rp > 0) prices.push(rp);
      }
    });
  }

  if (prices.length === 0) {
    return { min: basePrice, max: basePrice, text: `₹${basePrice.toLocaleString()}`, hasRange: false };
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (min === max) {
    return { min, max, text: `₹${min.toLocaleString()}`, hasRange: false };
  }

  return { min, max, text: `₹${min.toLocaleString()} - ₹${max.toLocaleString()}`, hasRange: true };
}

/**
 * Normalizes any time string (e.g. "18:00", "6:00 PM", "06:00 AM", "6", "18:30:00")
 * into { hour: "06", minute: "00", ampm: "PM" }
 */
export function parseTimeTo12h(timeStr) {
  if (!timeStr) return { hour: '06', minute: '00', ampm: 'AM' };
  const str = String(timeStr).trim();

  // Check 12-hour format with AM/PM
  const match12 = str.match(/(\d+)(?::(\d+))?\s*(AM|PM)?/i);
  if (match12) {
    let h = parseInt(match12[1], 10);
    const m = match12[2] ? String(match12[2]).padStart(2, '0') : '00';
    let period = match12[3] ? match12[3].toUpperCase() : null;

    if (!period) {
      // If no AM/PM, determine by hour value (24-hour style)
      if (h >= 12) {
        period = 'PM';
        if (h > 12) h -= 12;
      } else {
        period = 'AM';
        if (h === 0) h = 12;
      }
    } else {
      if (h === 0) h = 12;
      if (h > 12) {
        period = 'PM';
        h -= 12;
      }
    }

    const hStr = String(h).padStart(2, '0');
    return { hour: hStr, minute: m, ampm: period || 'AM' };
  }

  return { hour: '06', minute: '00', ampm: 'AM' };
}

/**
 * Format 12-hour components to "06:00 AM"
 */
export function format12hTime(hour, minute, ampm) {
  const h = String(parseInt(hour || '6', 10)).padStart(2, '0');
  const m = String(minute || '00').padStart(2, '0');
  const p = (ampm || 'AM').toUpperCase();
  return `${h}:${m} ${p}`;
}

/**
 * Converts "06:00 AM" to total minutes from midnight (0 to 1440)
 */
export function time12hToMinutes(timeStr, isEndTime = false) {
  if (!timeStr) return 0;
  const { hour, minute, ampm } = parseTimeTo12h(timeStr);
  let h = parseInt(hour, 10);
  const m = parseInt(minute, 10);

  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;

  let total = h * 60 + m;
  // If end time is 12:00 AM (midnight next day) and we want it to close after night
  if (isEndTime && total === 0) {
    total = 1440; // 24:00 (Midnight)
  }
  return total;
}

/**
 * Parses a slot label e.g. "06:00 AM - 07:00 AM" or "06:00 AM to 06:00 PM"
 */
export function parseSlotRangeToMinutes(slotStr) {
  if (!slotStr) return null;
  const parts = String(slotStr).split(/\s*(?:-|–|to)\s*/i);
  if (parts.length < 2) return null;

  const startMins = time12hToMinutes(parts[0], false);
  let endMins = time12hToMinutes(parts[1], true);

  if (endMins <= startMins) {
    endMins += 1440; // Spans past midnight
  }

  return { start: startMins, end: endMins, startStr: parts[0], endStr: parts[1] };
}

/**
 * Generates an array of 1-hour slots from startTime to endTime with the specified price
 */
export function generateSlotsFromRange(startStr, endStr, pricePerSlot = null, stepMinutes = 60) {
  if (!startStr || !endStr) return [];
  const startMins = time12hToMinutes(startStr, false);
  let endMins = time12hToMinutes(endStr, true);

  if (startMins === endMins) {
    return [];
  }

  // If end time is before start time (e.g. 10:00 PM to 02:00 AM next day)
  if (endMins < startMins) {
    endMins += 1440;
  }

  const slots = [];
  let current = startMins;

  while (current < endMins) {
    const next = Math.min(current + stepMinutes, endMins);
    
    // Format start
    const startH = Math.floor((current % 1440) / 60);
    const startM = (current % 1440) % 60;
    const startPeriod = startH >= 12 ? 'PM' : 'AM';
    let startDisplayH = startH % 12;
    if (startDisplayH === 0) startDisplayH = 12;
    const startLabel = `${String(startDisplayH).padStart(2, '0')}:${String(startM).padStart(2, '0')} ${startPeriod}`;

    // Format end
    const endRawH = Math.floor(next / 60);
    const endH = Math.floor((next % 1440) / 60);
    const endM = (next % 1440) % 60;
    const endPeriod = (next % 1440 === 0 && endRawH > 0) ? 'AM' : endH >= 12 ? 'PM' : 'AM';
    let endDisplayH = endH % 12;
    if (endDisplayH === 0) endDisplayH = 12;
    const endLabel = `${String(endDisplayH).padStart(2, '0')}:${String(endM).padStart(2, '0')} ${endPeriod}`;

    const slotObj = {
      label: `${startLabel} - ${endLabel}`,
      isActive: true,
    };
    if (pricePerSlot !== null && pricePerSlot !== '' && !isNaN(parseFloat(pricePerSlot))) {
      slotObj.price = parseFloat(pricePerSlot);
    }

    slots.push(slotObj);
    current = next;
  }

  return slots;
}

/**
 * Deduplicates and sorts slots chronologically
 */
export function sortAndDeduplicateSlots(slotsList, defaultPrice = 0) {
  if (!Array.isArray(slotsList)) return [];
  const seen = new Set();
  const result = [];

  for (const s of slotsList) {
    const label = typeof s === 'string' ? s : (s?.label || s?.slot || '');
    if (!label || seen.has(label)) continue;
    seen.add(label);

    const price = (typeof s === 'object' && s?.price !== undefined && s?.price !== '')
      ? parseFloat(s.price)
      : parseFloat(defaultPrice) || 0;
    const isActive = typeof s === 'object' && s?.isActive !== undefined ? s.isActive : true;

    result.push({
      label,
      isActive,
      price
    });
  }

  result.sort((a, b) => {
    const rangeA = parseSlotRangeToMinutes(a.label);
    const rangeB = parseSlotRangeToMinutes(b.label);
    if (!rangeA || !rangeB) return 0;
    return rangeA.start - rangeB.start;
  });

  return result;
}

/**
 * Interactive 12-Hour Clock Time Picker Component with AM/PM
 */
export default function TimePicker12({
  value,
  onChange,
  label,
  style = {},
  disabled = false,
  compact = false
}) {
  const parsed = parseTimeTo12h(value);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [ampm, setAmpm] = useState(parsed.ampm);

  useEffect(() => {
    const p = parseTimeTo12h(value);
    setHour(p.hour);
    setMinute(p.minute);
    setAmpm(p.ampm);
  }, [value]);

  const updateTime = (h, m, p) => {
    const formatted = format12hTime(h, m, p);
    onChange?.(formatted);
  };

  const handleHourChange = (e) => {
    const newH = e.target.value;
    setHour(newH);
    updateTime(newH, minute, ampm);
  };

  const handleMinuteChange = (e) => {
    const newM = e.target.value;
    setMinute(newM);
    updateTime(hour, newM, ampm);
  };

  const handleAmpmToggle = (newAmpm) => {
    setAmpm(newAmpm);
    updateTime(hour, minute, newAmpm);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', ...style }}>
      {label && (
        <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </label>
      )}

      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? '4px' : '6px',
        background: '#FFFFFF',
        border: '1.5px solid #DCEFB8',
        borderRadius: compact ? '10px' : '12px',
        padding: compact ? '4px 6px' : '6px 10px',
        boxShadow: '0 2px 6px rgba(8,71,52,0.04)',
        boxSizing: 'border-box',
        width: '100%'
      }}>
        <Clock size={compact ? 14 : 16} color="#084734" style={{ flexShrink: 0, opacity: 0.8 }} />

        {/* Hour Dropdown */}
        <select
          value={hour}
          onChange={handleHourChange}
          disabled={disabled}
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontWeight: '800',
            fontSize: compact ? '0.85rem' : '0.92rem',
            color: '#084734',
            cursor: 'pointer',
            padding: '2px 0',
            fontFamily: 'inherit'
          }}
        >
          {HOURS_12.map(h => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>

        <span style={{ fontWeight: '800', color: '#084734', fontSize: '0.9rem' }}>:</span>

        {/* Minute Dropdown */}
        <select
          value={minute}
          onChange={handleMinuteChange}
          disabled={disabled}
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontWeight: '800',
            fontSize: compact ? '0.85rem' : '0.92rem',
            color: '#084734',
            cursor: 'pointer',
            padding: '2px 0',
            fontFamily: 'inherit'
          }}
        >
          {ALL_MINUTES.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        {/* AM/PM Switcher */}
        <div style={{
          display: 'flex',
          background: '#F1F5F0',
          borderRadius: '8px',
          padding: '2px',
          marginLeft: 'auto',
          flexShrink: 0
        }}>
          <button
            type="button"
            onClick={() => handleAmpmToggle('AM')}
            disabled={disabled}
            style={{
              border: 'none',
              borderRadius: '6px',
              padding: compact ? '3px 6px' : '4px 8px',
              fontSize: compact ? '0.72rem' : '0.75rem',
              fontWeight: '850',
              cursor: 'pointer',
              transition: 'all 0.15s',
              background: ampm === 'AM' ? '#084734' : 'transparent',
              color: ampm === 'AM' ? '#CEF17B' : '#64748B',
              boxShadow: ampm === 'AM' ? '0 2px 4px rgba(8,71,52,0.2)' : 'none'
            }}
          >
            AM
          </button>
          <button
            type="button"
            onClick={() => handleAmpmToggle('PM')}
            disabled={disabled}
            style={{
              border: 'none',
              borderRadius: '6px',
              padding: compact ? '3px 6px' : '4px 8px',
              fontSize: compact ? '0.72rem' : '0.75rem',
              fontWeight: '850',
              cursor: 'pointer',
              transition: 'all 0.15s',
              background: ampm === 'PM' ? '#084734' : 'transparent',
              color: ampm === 'PM' ? '#CEF17B' : '#64748B',
              boxShadow: ampm === 'PM' ? '0 2px 4px rgba(8,71,52,0.2)' : 'none'
            }}
          >
            PM
          </button>
        </div>
      </div>
    </div>
  );
}
